import BaseComponent from "./base_component_controller";

import { useResize } from "hb/insights/use_resize";
import { useChartConfig } from "hb/insights/use_chart_config";
import { dimensionType, valueFormatter } from "hb/insights/chart";
import {
  ensureResultsMonotonic,
  nestedGroupByToMap,
  foldedMap,
  extractCascade,
} from "hb/lib/collection_helpers";
import { isDatetimeType, isNumberType } from "hb/insights/types";

export default class extends BaseComponent {
  static viewId = "histogram";

  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object,
    };
  }

  static configs = [
    "xField",
    "xFieldUnit",
    "yField",
    "yFieldUnit",

    "zField",
    { name: "groups", default: "{}", type: "json" },
  ];

  connect() {
    super.connect();

    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected();

    useResize(this, { element: this.outletComponent("chart").chartTarget });
    this.initQuery();
  }

  renderConfig() {
    const { meta } = this.query;

    ["xField", "yField", "zField"].forEach((name) => {
      let allFields;
      if (name === "zField") {
        allFields = ["__empty", ...meta.fields];
      } else {
        allFields = meta.fields;
      }

      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields
        .map((field) =>
          this.comboboxItemTemplate({
            classes: this[name] === field ? "current" : "",
            value: field,
            id: field,
            display: field,
            ...(field == "__empty" && {
              display: null,
              badge: "none",
            }),
          }),
        )
        .join("\n");
    });

    this.find(`input[name="xField"]`).value = this.config.xField || "";
    this.find(`input[name="yField"]`).value = this.config.yField || "";
    this.find(`input[name="zField"]`).value = this.config.zField || "";

    this.renderFieldGroups(this.derivedConfig.groupConfig);
  }

  validate() {
    let { xField, yField, zField } = this.config;

    if (!xField || !yField) {
      this.errorMessage =
        "Select fields in chart config or update data to fit chart";
    } else if (this.hasUnknownFields(xField, yField, zField)) {
      this.errorMessage = "Unknown field selected";
    }
  }

  queryContextDefaults() {
    const {
      meta: { fieldTypes: rawFieldTypes },
    } = this.query;
    let config = {};
    const fieldTypes = Object.entries(rawFieldTypes);

    const xRes = extractCascade(
      fieldTypes,
      ([, t]) => isDatetimeType(t),
      ([, t]) => isNumberType(t),
    );

    const yRes = extractCascade(
      xRes[1],
      ([, t]) => isNumberType(t),
      ([, t]) => isDatetimeType(t),
    );

    const zRes = extractCascade(yRes[1], ([, t]) => t === "string");

    if (xRes[0] !== undefined) config.xField = xRes[0][0];
    if (yRes[0] !== undefined) config.yField = yRes[0][0];
    if (zRes[0] !== undefined) config.zField = zRes[0][0];

    return config;
  }

  processConfig() {
    const {
      config: { yField, zField, groups },
      query: { results },
    } = this;
    let groupConfig;

    let colorIdx = -1;
    const gatherColor = (n) => {
      colorIdx = (colorIdx + 1) % this.colorValues.length;
      return groups[n]?.color || this.colorValues[colorIdx];
    };

    if (zField) {
      const zDomain = Array.from(new Set(results.map((d) => d.field(zField))));
      this.derivedConfig.zDomain = zDomain;
      groupConfig = zDomain.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
      }));
    } else {
      groupConfig = [
        {
          idx: 0,
          name: yField,
          color: gatherColor(yField),
        },
      ];
    }

    this.derivedConfig.groupConfig = groupConfig;
  }

  render() {
    let { meta, results } = this.query;
    const { xField, xFieldUnit, yField, yFieldUnit, zField } = this.config;

    if (!xField || !yField || !this.query) {
      return;
    }

    results = ensureResultsMonotonic(results, xField);

    let dataset, series, yAxis, xAxis;

    const xFieldType = meta.type(xField);

    const barDefault = {
      type: "bar",
      barWidth: "99.8%",
      stack: "default",
      animation: false,
    };

    if (isDatetimeType(xFieldType) && results.length === 1) {
      barDefault.barWidth = "20px";
    }

    if (zField) {
      const zDomain = Array.from(new Set(results.map((d) => d.field(zField))));

      const nested = nestedGroupByToMap(
        results,
        (r) => [r.field(xField), r.field(zField)],
        (r) => r.field(yField),
      );

      const folded = foldedMap(nested, xField);

      dataset = {
        source: folded,
        dimensions: [{ name: "", type: dimensionType(xFieldType) }, ...zDomain],
      };

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color }) => ({
          ...barDefault,
          itemStyle: { color },
          name,
          dimensions: [xField, name],
        }));

      yAxis = { name: "" };
    } else {
      dataset = {
        dimensions: [xField, yField],
        source: results.map((r) => r.slice(yField, xField)),
      };
      series = [
        {
          ...barDefault,
          itemStyle: { color: this.derivedConfig.groupConfig[0].color },
          name: yField,
        },
      ];
      yAxis = { name: "" };
    }

    xAxis = {
      type: "category",
      name: "",
      min: "dataMin",
      max: "dataMax",
    };

    if (isDatetimeType(xFieldType)) {
      xAxis.type = "time";

      if (meta.queryStartAt) {
        // Use the earliest date between the dataset and the query
        let dateMin = results.reduce(
          (min, r) => (min < r.parsed[xField] ? min : r.parsed[xField]),
          new Date(meta.queryStartAt),
        );
        xAxis.min = dateMin;
      }

      if (meta.queryEndAt) {
        // Use the latest date between the dataset and the query
        let dateMax = results.reduce(
          (max, r) => (max > r.parsed[xField] ? max : r.parsed[xField]),
          new Date(meta.queryEndAt),
        );
        xAxis.max = dateMax;
      }
    }

    let defaults = this.defaultChartConfig;

    let tooltip = {
      ...defaults.tooltip,
      trigger: "axis",
    };

    const xFieldFormatter = valueFormatter(xFieldUnit);
    if (xFieldFormatter) {
      xAxis.axisLabel = { formatter: xFieldFormatter };
    }

    const yFieldFormatter = valueFormatter(yFieldUnit);
    if (yFieldFormatter) {
      yAxis.axisLabel = { formatter: yFieldFormatter };
    }

    const options = {
      ...defaults,
      dataset,
      series,
      yAxis,
      xAxis: { ...defaults.xAxis, ...xAxis },
      tooltip,
    };

    this.outletComponent("chart").setChartOptions(options);
    this.outletComponent("legend").render(this.derivedConfig.groupConfig);
  }
}
